// Navigation items of the shell parts (009 revision 6, FR-046): the one shape every header, bottom bar, section
// navigation and route tab bar takes, the link component the app supplies (its router's `Link`), and the rule that
// marks the current item from the current path. No app route or label is written here; the apps pass them.
import type { ComponentType, ReactNode } from 'react';

/** The link component an app passes (its router's `Link`), or a plain anchor. */
export type LinkLike = ComponentType<{
  to: string;
  className?: string;
  'aria-current'?: 'page';
  children: ReactNode;
}>;

/** One navigation item. */
export interface ShellNavItem {
  /** Where the item leads. */
  to: string;
  label: string;
  /** An icon drawn before the label (the bottom bar draws it above). */
  icon?: ReactNode;
  /** A short note after the label, for example "Not connected". */
  badge?: string;
  /** The path prefix whose pages mark the item as current (default: `to`). */
  prefix?: string;
  /** Marks the item as current regardless of the path. */
  current?: boolean;
  /** The design layer the item implements, for the design-coverage report (`data-design`; 000 revision 21). */
  design?: string;
}

/**
 * Whether an item is the current one: marked so, or the current path is its prefix or below it.
 * @param item the item
 * @param currentPath the current path, when known
 * @returns true when the item is current
 */
export function isCurrent(item: ShellNavItem, currentPath?: string): boolean {
  if (item.current !== undefined) return item.current;
  if (currentPath === undefined) return false;
  const prefix = item.prefix ?? item.to;
  return (
    currentPath === prefix || currentPath.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`)
  );
}

/**
 * A plain anchor, the link component when the app passes none.
 * @param props the address, classes, current mark and content
 * @returns the anchor
 */
export const PlainLink: LinkLike = ({ to, children, ...rest }) => (
  <a href={to} {...rest}>
    {children}
  </a>
);
